import React from 'react';
import { Box } from '@mui/material';
import { Avatar } from '@rentspree/component-2023.components.atoms.avatar';
import { Badge } from '@rentspree/component-2023.components.atoms.badge';
import { Button } from '@rentspree/component-2023.components.atoms.button';
import { Typography } from '@rentspree/component-2023.components.atoms.typography';
import { InteractiveCard } from '@rentspree/component-2023.components.organisms.interactive-card';

import { Application, DecisionType } from '../../types/application';

import { VerificationIndicators } from './VerificationIndicators';

export interface ApplicationCardProps {
  application: Application;
  onMakeDecision?: (applicationId: string, decision: DecisionType) => void;
  onCreateLease?: (applicationId: string) => void;
  className?: string;
}

export const ApplicationCard: React.FC<ApplicationCardProps> = ({
  application,
  onMakeDecision,
  onCreateLease,
  className,
}) => {
  const { applicant, status, decision, appliedAt } = application;

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(date);
  };

  const getDecisionBadgeProps = () => {
    switch (decision) {
      case DecisionType.ACCEPTED:
        return {
          children: 'Accepted',
          sx: {
            backgroundColor: '#dcfce7', // green-100 equivalent
            color: '#34b844',
            fontWeight: 500,
            fontSize: '12px',
            lineHeight: '16px',
            padding: '3px 8px',
            borderRadius: '9999px',
            textTransform: 'none' as const,
            minWidth: 'auto',
            height: 'auto',
          },
        };
      case DecisionType.REJECTED:
        return {
          children: 'Rejected',
          sx: {
            backgroundColor: '#e5e7eb', // gray-200 equivalent - matching Figma design
            color: '#6b7280', // gray-500 equivalent - matching Figma design
            fontWeight: 500,
            fontSize: '12px',
            lineHeight: '16px',
            padding: '3px 8px',
            borderRadius: '9999px',
            textTransform: 'none' as const,
            minWidth: 'auto',
            height: 'auto',
          },
        };
      default:
        return null;
    }
  };

  const getActionButton = () => {
    if (status === 'decision_made' && decision === DecisionType.ACCEPTED) {
      return (
        <Button
          variant='contained'
          color='primary'
          size='small'
          onClick={() => onCreateLease?.(application.id)}
          sx={{
            backgroundColor: '#1f15e1', // Updated to match Figma design
            color: '#ffffff',
            fontWeight: 600,
            fontSize: '14px',
            borderRadius: '10px', // Updated to match Figma design
            textTransform: 'none',
            height: 36,
            width: '100%', // Full width expansion
            padding: '8px 16px',
            '&:hover': {
              backgroundColor: '#1a12c7', // Darker shade for hover
            },
          }}
        >
          Create Lease
        </Button>
      );
    }

    if (status === 'in_review') {
      return (
        <Button
          variant='outlined'
          color='primary'
          size='small'
          onClick={() => onMakeDecision?.(application.id, DecisionType.PENDING)}
          sx={{
            color: '#374151',
            borderColor: '#d1d5db',
            fontWeight: 600,
            fontSize: '14px',
            borderRadius: '6px',
            textTransform: 'none',
            height: 36,
            width: '100%', // Full width expansion for consistency
            padding: '8px 16px',
            '&:hover': {
              borderColor: '#9ca3af',
              backgroundColor: '#f9fafb',
            },
          }}
        >
          Make Decision
        </Button>
      );
    }

    return null;
  };

  const decisionBadgeProps = getDecisionBadgeProps();

  const renderFullLayout = () => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      {/* Top section: Avatar + Info + Badge */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            flex: 1,
          }}
        >
          <Avatar
            {...(applicant.profileImage ? { src: applicant.profileImage } : {})}
            size='large'
            sx={{
              width: 48,
              height: 48,
              fontSize: '18px',
              fontWeight: 600,
            }}
          >
            {applicant.name
              .split(' ')
              .map(n => n[0])
              .join('')
              .toUpperCase()}
          </Avatar>

          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant='h6'
              sx={{
                color: '#222222',
                fontWeight: 600,
                fontSize: '16px',
                lineHeight: '24px',
                marginBottom: '2px',
              }}
            >
              {applicant.name}
            </Typography>
            <Typography
              variant='body2'
              sx={{
                color: '#6b7280',
                fontSize: '14px',
                lineHeight: '20px',
              }}
            >
              Applied: {formatDate(appliedAt)}
            </Typography>
          </Box>
        </Box>

        {/* Decision badge positioned on the right side of top section */}
        {decisionBadgeProps && <Badge {...decisionBadgeProps} />}
      </Box>

      {/* Middle section: Score + Verification - Hidden for NEW status */}
      {status !== 'new' && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            backgroundColor: '#f9fafb', // gray-50 background to match Figma design
            borderRadius: '8px',
            padding: '16px',
            margin: '0 -8px', // Extend slightly beyond card padding for better visual integration
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
            <Box
              sx={{
                position: 'relative',
                textAlign: 'center',
                minWidth: '40px',
              }}
            >
              <Typography
                variant='h6'
                sx={{
                  color: '#222222',
                  fontWeight: 600,
                  fontSize: '16px',
                  lineHeight: '24px',
                  marginBottom: '2px',
                }}
              >
                {applicant.creditScore}
              </Typography>
              <Typography
                variant='caption'
                sx={{
                  color: '#6b7280',
                  fontSize: '12px',
                  lineHeight: '16px',
                }}
              >
                Score
              </Typography>
            </Box>

            <Box
              sx={{
                position: 'relative',
                textAlign: 'center',
                minWidth: '55px',
              }}
            >
              <Typography
                variant='h6'
                sx={{
                  color: '#222222',
                  fontWeight: 600,
                  fontSize: '16px',
                  lineHeight: '24px',
                  marginBottom: '2px',
                }}
              >
                {applicant.incomeRatio.toFixed(1)}x
              </Typography>
              <Typography
                variant='caption'
                sx={{
                  color: '#6b7280',
                  fontSize: '12px',
                  lineHeight: '16px',
                }}
              >
                IR Ratio
              </Typography>
            </Box>
          </Box>

          <VerificationIndicators
            housingVerified={applicant.housingVerified}
            identityVerified={applicant.identityVerified}
            incomeVerified={applicant.incomeVerified}
          />
        </Box>
      )}

      {/* Bottom section: Action Button - Hidden for NEW status */}
      {status !== 'new' && getActionButton() && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 1,
          }}
        >
          {getActionButton()}
        </Box>
      )}
    </Box>
  );

  return (
    <InteractiveCard
      className={className || ''}
      sx={{
        marginBottom: '16px',
        border: '1px solid #f3f4f6',
        borderRadius: '12px',
        backgroundColor: '#ffffff',
        boxShadow: '0px 1px 2px 0px rgba(0,0,0,0.05)',
        minHeight: '98px',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        '&:hover': {
          boxShadow:
            '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          transition: 'all 0.2s ease-in-out',
        },
      }}
    >
      <Box
        sx={{
          paddingLeft: '24px',
          paddingRight: '24px',
        }}
      >
        {renderFullLayout()}
      </Box>
    </InteractiveCard>
  );
};
